import React, { FC, useCallback, useMemo } from 'react';
import { Checkbox, Theme, styled, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { TargetedStudentData, TargetedStudents } from '../../types';

type RefinedTargetAudienceProps = {
  targetedStudents: TargetedStudents;
  onRefineStudent: (targetedStudent: TargetedStudentData, newValue: boolean) => void;
};

const Wrapper = styled('div')<Theme>(({ theme }) => ({
  display: 'flex',
  paddingTop: theme.spacing(1),
}));

const Column = styled('div')<Theme, { numcolumns: number }>(({ theme, numcolumns }) => ({
  width: `${100 / numcolumns}%`,
  padding: theme.spacing(0, 1),
  '&:first-child': {
    paddingLeft: 0,
  },
  '&:last-child': {
    paddingRight: 0,
  },
}));

const StudentTile = styled('div')<Theme>(({ theme }) => ({
  background: theme.palette.common.white,
  '&.odd': {
    background: theme.palette.grey['50'],
  },
  cursor: 'pointer',
}));

export const RefinedTargetAudience: FC<RefinedTargetAudienceProps> = ({
  targetedStudents,
  onRefineStudent,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
  const tablet = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const numColumns = mobile ? 2 : tablet ? 3 : 4;
  const numPerColumn = Math.ceil(Object.keys(targetedStudents).length / numColumns);

  const list = useMemo(() => {
    const result: TargetedStudentData[][] = [];

    let i = 0,
      temp: TargetedStudentData[] = [];
    Object.entries(targetedStudents)
      .sort((a, b) => (a[1].student.fullName > b[1].student.fullName ? 1 : -1))
      .forEach((entry) => {
        if (i === numPerColumn) {
          i = 0;
          result.push(temp);
          temp = [];
        }
        temp.push(entry[1]);
        i++;
      });

    result.push(temp);
    return result;
  }, [targetedStudents, numPerColumn]);

  const handleTargetedStudentChange = useCallback(
    (targetedStudent: TargetedStudentData) => {
      onRefineStudent(targetedStudent, !targetedStudent.value);
    },
    [onRefineStudent]
  );

  return Object.keys(targetedStudents).length > 0 ? (
    <Wrapper>
      {list.map((subList: TargetedStudentData[], index) => (
        <Column numcolumns={numColumns} key={index}>
          {subList.map((targetedStudent, index) => (
            <StudentTile
              className={index % 2 === 0 ? 'odd' : undefined}
              key={index}
              onClick={() => handleTargetedStudentChange(targetedStudent)}
            >
              <Checkbox checked={targetedStudent.value} color="primary" />
              {`${targetedStudent.student.fullName}`}
            </StudentTile>
          ))}
        </Column>
      ))}
    </Wrapper>
  ) : (
    <div>{t('feedback.targetAudience.noStudentsInGroup')}</div>
  );
};
